import { db, storage } from "../firebase";
import {
  collection,
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  query,
  orderBy,
  serverTimestamp,
} from "firebase/firestore";
import {
  ref,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { uploadOptimizedImage } from "@/lib/uploadImage";
import { sortByOrder } from "./reorderService";

const COURSES_COLLECTION = "course";

/** Generates a fresh id for a new course draft — reused across image
 * upload + final submit, same pattern as generateFerryId/generateTransportId. */
export function generateCourseId() {
  return doc(collection(db, COURSES_COLLECTION)).id;
}

export async function createCourse(id, payload) {
  await setDoc(doc(db, COURSES_COLLECTION, id), {
    ...payload,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return id;
}

export async function updateCourse(id, payload) {
  await updateDoc(doc(db, COURSES_COLLECTION, id), {
    ...payload,
    updatedAt: serverTimestamp(),
  });
  return id;
}

export async function deleteCourse(id, imageUrl) {
  await deleteDoc(doc(db, COURSES_COLLECTION, id));
  if (imageUrl) {
    try {
      await deleteObject(ref(storage, imageUrl));
    } catch (err) {
      // Image may already be gone or URL may not be a storage ref
      // (e.g. external URL) — don't block the delete on cleanup failing.
      console.warn("Course image cleanup failed:", err);
    }
  }
}

export async function getAllCourses() {
  const q = query(collection(db, COURSES_COLLECTION), orderBy("createdAt", "desc"));
  const snap = await getDocs(q);
  const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  return sortByOrder(items);
}

/** Uploads/replaces the main photo for a course. If replacing, the old
 * image at existingUrl is deleted after the new one succeeds. */
export async function uploadCourseImage(id, file, existingUrl) {
  const storageRef = ref(
    storage,
    `courses/${id}/main_${Date.now()}_${file.name}`,
  );
  const snap = await uploadOptimizedImage(storageRef, file);
  const url = await getDownloadURL(snap.ref);

  if (existingUrl) {
    try {
      await deleteObject(ref(storage, existingUrl));
    } catch (err) {
      console.warn("Old course image cleanup failed:", err);
    }
  }

  return url;
}

/** Uploads a gallery photo for a course. Gallery items don't get replaced
 * in place — GalleryUpload just appends new ones and lets the admin
 * remove individual entries — so no existingUrl/replace param here. */
export async function uploadCourseGalleryImage(id, file) {
  const storageRef = ref(
    storage,
    `courses/${id}/gallery_${Date.now()}_${file.name}`,
  );
  const snap = await uploadOptimizedImage(storageRef, file);
  return getDownloadURL(snap.ref);
}